
import { LoadingPriority, LoadingState } from './types';
import { NetworkDetector } from './NetworkDetector';
import { RequestManager } from './RequestManager';

/**
 * Main manager that coordinates loading state across the application
 */
export class LoadingStateManager {
  private static instance: LoadingStateManager;
  private listeners: ((state: LoadingState) => void)[] = [];
  private currentState: LoadingState = {
    isLoading: false,
    pendingRequests: [],
  };
  
  private networkDetector: NetworkDetector;
  private requestManager: RequestManager;

  private constructor() {
    this.networkDetector = new NetworkDetector();
    this.requestManager = new RequestManager(this.networkDetector);
    
    // Check network conditions initially
    this.networkDetector.checkNetworkConditions();
  }

  public static getInstance(): LoadingStateManager {
    if (!LoadingStateManager.instance) {
      LoadingStateManager.instance = new LoadingStateManager();
    }
    return LoadingStateManager.instance;
  }

  /**
   * Add a new loading request
   */
  public addRequest(
    name: string,
    priority: LoadingPriority = 'normal',
    dependencies?: string[]
  ): string {
    const id = this.requestManager.addRequest(name, priority, dependencies);
    this.updateState();
    return id;
  }

  /**
   * Complete a loading request
   */
  public completeRequest(id: string): void {
    this.requestManager.completeRequest(id);
    this.updateState();
  }

  /**
   * Subscribe to loading state changes
   */
  public subscribe(listener: (state: LoadingState) => void): () => void {
    this.listeners.push(listener);
    // Immediately notify the new listener of the current state
    listener(this.currentState);
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /**
   * Update current loading state and notify listeners
   */
  private updateState(): void {
    const pendingRequestsArray = this.requestManager.getPendingRequests();
    const isLoading = pendingRequestsArray.length > 0;
    
    // Set loading message based on highest priority request
    let loadingMessage = undefined;
    if (isLoading && pendingRequestsArray.length > 0) {
      const highestPriorityRequest = pendingRequestsArray[0];
      loadingMessage = `Loading ${highestPriorityRequest.name}...`;
    }

    this.currentState = {
      isLoading,
      pendingRequests: pendingRequestsArray,
      loadingMessage,
    };

    // Notify all listeners
    this.listeners.forEach((listener) => listener(this.currentState));
  }

  /**
   * Clear all loading requests
   */
  public clearAllRequests(): void {
    this.requestManager.clearAllRequests();
    this.updateState();
  }

  /**
   * Get current network status
   */
  public getNetworkStatus(): { isLowBandwidth: boolean } {
    return this.networkDetector.getNetworkStatus();
  }

  /**
   * Create a batch of related requests
   */
  public createBatch(
    requests: Array<{ name: string; priority?: LoadingPriority }>,
    batchName: string
  ): Array<string> {
    return this.requestManager.createBatch(requests, batchName);
  }

  /**
   * Complete a batch of requests
   */
  public completeBatch(requestIds: string[]): void {
    this.requestManager.completeBatch(requestIds);
    this.updateState();
  }
}
